import React, { createContext, useContext, useRef } from "react";
import { useAuth } from "../firebase/AuthProvider"; // Assuming you have an AuthProvider

// Define the shape of an avatar entry
// Now stores the base64 string directly
type AvatarEntry = {
  base64Image: string | null; // Changed from blobUrl
  expiresAt: number;
};

interface AvatarContextType {
  getAvatarUrl: (userId: string) => Promise<string | null>;
  preloadAvatars: (userIds: string[]) => void;
  refreshAvatar: (userId: string) => void; // <-- Add this
}

const AvatarContext = createContext<AvatarContextType | null>(null);

export const AvatarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth(); // Get current user from your AuthProvider
  const avatarCache = useRef<Map<string, AvatarEntry>>(new Map());
  const pendingFetches = useRef<Map<string, Promise<string | null>>>(new Map());

  /**
   * Fetches the avatar image as a Base64 string from your API.
   * Your API is responsible for fetching from Azure Blob Storage and encoding.
   * @param userId The ID of the user whose avatar is to be fetched.
   * @returns A Promise that resolves to the Base64 image URL (data URI) or null if an error occurs.
   */
  const fetchAvatarBase64 = async (userId: string): Promise<string | null> => {
    try {
      const authToken = await currentUser?.getIdToken();
if(!userId || userId === '' || userId === 'undefined'){
  return null;
}
      // Your API endpoint that returns the Base64 image
      // This API call is now responsible for proxying the image from Azure Blob Storage
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/file/avatar/${userId}`, // Example new endpoint
        {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!res.ok) {
        // If the API indicates no avatar or an error
        if (res.status === 404) {
          throw new Error("Avatar not found for this user.");
        }
        throw new Error(`API error: ${res.statusText}`);
      }

      // Assuming your API returns a JSON object like { base64Image: "data:image/png;base64,..." }
      const { base64Image, expiresInSeconds } = await res.json();

      if (!base64Image) {
        throw new Error("Base64 image data not received from API.");
      }

      // Calculate expiration time for caching
      // Subtract a buffer (e.g., 5 seconds) to ensure it's still valid when used
      const expiresAt = Date.now() + expiresInSeconds * 1000 - 5000;

      // Cache the Base64 image string
      avatarCache.current.set(userId, { base64Image, expiresAt });

      return base64Image;
    } catch (err) {
      console.warn(`[Avatar] Failed to fetch Base64 for ${userId}:`, err);

      // Reduce negative cache time from 5 minutes to 30 seconds to allow quicker retry
      avatarCache.current.set(userId, {
        base64Image: null, // Store null for failed attempts
        expiresAt: Date.now() + 30 * 1000, // Cache negative result for 30 seconds
      });

      return null;
    }
  };

  /**
   * Retrieves the avatar URL (Base64 data URI) from cache or fetches it if not present or expired.
   * Handles concurrent requests for the same avatar.
   * @param userId The ID of the user whose avatar URL is to be retrieved.
   * @returns A Promise that resolves to the Base64 data URI string or null.
   */
  const getAvatarUrl = async (userId: string): Promise<string | null> => {
    const entry = avatarCache.current.get(userId);

    // If entry exists and is not expired, return cached value
    if (entry && entry.expiresAt > Date.now()) {
      return entry.base64Image;
    }

    // If a fetch is already in progress for this user, return that promise
    if (pendingFetches.current.has(userId)) {
      return pendingFetches.current.get(userId)!;
    }

    // Otherwise, start a new fetch and store its promise
    const fetchPromise = fetchAvatarBase64(userId);
    pendingFetches.current.set(userId, fetchPromise);

    const result = await fetchPromise;
    pendingFetches.current.delete(userId); // Remove promise once resolved/rejected

    return result;
  };

  /**
   * Preloads avatars for a list of user IDs.
   * This is a "fire-and-forget" operation, primarily for improving perceived performance.
   * @param userIds An array of user IDs to preload avatars for.
   */
  const preloadAvatars = (userIds: string[]) => {
    userIds.forEach((id) => {
      const entry = avatarCache.current.get(id);
      // Only preload if not in cache or expired
      if (!entry || entry.expiresAt < Date.now()) {
        // Use getAvatarUrl to leverage pending fetch logic and caching
        getAvatarUrl(id); // Fire-and-forget
      }
    });
  };

  // Add this function
  const refreshAvatar = (userId: string) => {
    avatarCache.current.delete(userId);
    pendingFetches.current.delete(userId);
  };

  return (
    <AvatarContext.Provider value={{ getAvatarUrl, preloadAvatars, refreshAvatar }}>
      {children}
    </AvatarContext.Provider>
  );
};

/**
 * Custom hook to consume the AvatarContext.
 * Throws an error if used outside of an AvatarProvider.
 * @returns The AvatarContextType value.
 */
export const useAvatarCache = (): AvatarContextType => {
  const ctx = useContext(AvatarContext);
  if (!ctx) {
    throw new Error("useAvatarCache must be used inside AvatarProvider");
  }
  return ctx;
};
